import React, { memo, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MessageTypeConstants } from '../../../services/bot/Message';
import { getAuthData, updateDataInStorage } from '../../../services/storage/LocalStorage';
import { useDispatch, useSelector } from 'react-redux';
import { resetTabStack, setTabState } from '../../../state/actions/tabStack';
import { setBotWaitLoader, setBotWaitLoaderWithShimmer } from '../../../state/actions/loader';
import { processSendMessage } from '../../../services/bot/conversation/SendMessage';
import { setAddManuallayRow } from '../../../state/actions/filters';
import {  getPageName, uuidGenerator } from '../../../utils/general';
import { Box, CircularProgress } from '@mui/material';
import { ImagePath } from '../../../assets/ImagePath';

import CustomToolbar from '../../common/CustomToolBar';
import config from '../../../utils/config/config';
import CachedImage from '../../common/CachedImage';
import MobileBanner from './mobileBanners';
import { setSelectedBot } from '../../../state/actions/domain';
import { clearSelectedConversation, createChatBotConversation, setSelectedOnetoOneChat } from '../../../state/actions/chats';
import { onHomeClick, setHeaderTitle } from '../../../state/actions/layout';
import { withOutInitSocket } from '../../../state/actions/socket';
import { LFStorageKeys } from '../../../services/storage/LFStorage';
import { setCloseAssistantBot, setHideChatwidget, setHideDefaultHomepageBot } from '../../../state/actions/bot';
import { useNavigate } from 'react-router-dom';


const FMCatalog = ({ message, ...props }) => {
    const { options, rows } = message;

    // Memoized console log for debugging message
    useMemo(() => console.log('FMCatalog message : => ', message), [message]);

    // Handle the actual data structure - rows are nested in message property
    const actualRows = useMemo(() => {
        return message.message ? Object.values(message.message) : (rows || []);
    }, [message.message, rows]);

    const parentTabId = props.parentTabId ? props.parentTabId : options.tabId;
    const messageType = props.messageType ?? message?.messageType ?? MessageTypeConstants.MESSAGE_TYPE_CATALOG;
    const conversation = props.conversation ?? message?.conversation;
    const parentDocId = props.parentDocId ?? options.parentDocId;
    const botParamsRaw = getAuthData();
    const selectedBotDetails = useSelector((state) => state.domain.selectedBotDetails);
    const conversationId =
        botParamsRaw?.auth?.conversationId ||
        botParamsRaw?.auth?.newUser?.conversationId ||
        null;

    const [localRows, setLocalRows] = useState(actualRows || []);
    const [isInfiniteLoading, setIsInfiniteLoading] = useState(false);

    const scrollContainerRef = useRef(null);
    const pageCacheRef = useRef({});
    const isScrollRequestRef = useRef(false);
    const scrollCooldownRef = useRef(false);
    const scrollCooldownTimerRef = useRef(null);
    const SCROLL_COOLDOWN_MS = 600;
    const pathname = getPageName();
    const navigate = useNavigate();
    const defaultUserTimezone = "Etc/UTC";
    const { user = {} } = getAuthData();
    const dispatch = useDispatch();
    

    const renderButtons = (view, style) => {
        switch (view) {
            case 'calendar':
                return (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={style === view ? { fill: '#2DC0EF', fillOpacity: 1 } : { fill: '#dedede', fillOpacity: 0.2 }}>
                        <path d="M13.1666 10.4583C13.6268 10.4583 13.9999 10.0852 13.9999 9.62499C13.9999 9.16474 13.6268 8.79166 13.1666 8.79166C12.7063 8.79166 12.3333 9.16474 12.3333 9.62499C12.3333 10.0852 12.7063 10.4583 13.1666 10.4583Z" fill="currentColor" fill-opacity="0.2" />
                        <path d="M13.1666 13.7917C13.6268 13.7917 13.9999 13.4186 13.9999 12.9583C13.9999 12.4981 13.6268 12.125 13.1666 12.125C12.7063 12.125 12.3333 12.4981 12.3333 12.9583C12.3333 13.4186 12.7063 13.7917 13.1666 13.7917Z" fill="currentColor" fill-opacity="0.2" />
                        <path d="M9.83329 9.62499C9.83329 10.0852 9.46021 10.4583 8.99996 10.4583C8.53971 10.4583 8.16663 10.0852 8.16663 9.62499C8.16663 9.16474 8.53971 8.79166 8.99996 8.79166C9.46021 8.79166 9.83329 9.16474 9.83329 9.62499Z" fill="currentColor" fill-opacity="0.2" />
                        <path d="M9.83329 12.9583C9.83329 13.4186 9.46021 13.7917 8.99996 13.7917C8.53971 13.7917 8.16663 13.4186 8.16663 12.9583C8.16663 12.4981 8.53971 12.125 8.99996 12.125C9.46021 12.125 9.83329 12.4981 9.83329 12.9583Z" fill="currentColor" fill-opacity="0.2" />
                        <path d="M4.83333 10.4583C5.29357 10.4583 5.66667 10.0852 5.66667 9.62499C5.66667 9.16474 5.29357 8.79166 4.83333 8.79166C4.3731 8.79166 4 9.16474 4 9.62499C4 10.0852 4.3731 10.4583 4.83333 10.4583Z" fill="currentColor" fill-opacity="0.2" />
                        <path d="M4.83333 13.7917C5.29357 13.7917 5.66667 13.4186 5.66667 12.9583C5.66667 12.4981 5.29357 12.125 4.83333 12.125C4.3731 12.125 4 12.4981 4 12.9583C4 13.4186 4.3731 13.7917 4.83333 13.7917Z" fill="currentColor" fill-opacity="0.2" />
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M4.83329 0.25C5.17847 0.25 5.45829 0.529825 5.45829 0.875V1.5106C6.00996 1.49999 6.61773 1.49999 7.28618 1.5H10.7136C11.3821 1.49999 11.99 1.49999 12.5416 1.5106V0.875C12.5416 0.529825 12.8215 0.25 13.1666 0.25C13.5118 0.25 13.7916 0.529825 13.7916 0.875V1.56424C14.0082 1.58076 14.2134 1.60152 14.4075 1.62761C15.3845 1.75897 16.1753 2.03573 16.799 2.65937C17.4225 3.28301 17.6993 4.07379 17.8307 5.05082C17.9583 6.00015 17.9583 7.21317 17.9583 8.74458V10.5053C17.9583 12.0367 17.9583 13.2498 17.8307 14.1992C17.6993 15.1762 17.4225 15.967 16.799 16.5907C16.1753 17.2143 15.3845 17.491 14.4075 17.6224C13.4581 17.75 12.2451 17.75 10.7137 17.75H7.28628C5.75483 17.75 4.54178 17.75 3.59244 17.6224C2.61543 17.491 1.82463 17.2143 1.20099 16.5907C0.57736 15.967 0.300593 15.1762 0.169243 14.1992C0.0416013 13.2498 0.0416097 12.0368 0.0416264 10.5053V8.74467C0.0416097 7.21317 0.0416013 6.00016 0.169243 5.05082C0.300593 4.07379 0.57736 3.28301 1.20099 2.65937C1.82463 2.03573 2.61543 1.75897 3.59244 1.62761C3.78654 1.60152 3.99168 1.58076 4.20829 1.56424V0.875C4.20829 0.529825 4.48812 0.25 4.83329 0.25ZM3.759 2.86647C2.92059 2.97918 2.43755 3.19057 2.08488 3.54325C1.7322 3.89592 1.52081 4.37897 1.40809 5.21737C1.389 5.35936 1.37304 5.50884 1.35969 5.66667H16.6402C16.6269 5.50884 16.6109 5.35937 16.5918 5.21737C16.4791 4.37897 16.2677 3.89592 15.915 3.54325C15.5624 3.19057 15.0793 2.97918 14.241 2.86647C13.3845 2.75132 12.2556 2.75 10.6666 2.75H7.33329C5.74428 2.75 4.61538 2.75132 3.759 2.86647ZM1.29163 8.79167C1.29163 8.08 1.29189 7.46058 1.30253 6.91667H16.6974C16.708 7.46058 16.7083 8.08 16.7083 8.79167V10.4583C16.7083 12.0473 16.707 13.1762 16.5918 14.0327C16.4791 14.871 16.2677 15.3541 15.915 15.7067C15.5624 16.0594 15.0793 16.2708 14.241 16.3835C13.3845 16.4987 12.2556 16.5 10.6666 16.5H7.33329C5.74428 16.5 4.61538 16.4987 3.759 16.3835C2.92059 16.2708 2.43755 16.0594 2.08488 15.7067C1.7322 15.3541 1.52081 14.871 1.40809 14.0327C1.29295 13.1762 1.29163 12.0473 1.29163 10.4583V8.79167Z" fill="currentColor" />
                    </svg>
                );
            case 'map':
                return (
                    <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={style === view ? { fill: '#2DC0EF', fillOpacity: 1 } : { fill: '#dedede', fillOpacity: 0.2 }}>
                        <path d="M13.9999 0.708323H3.99995C3.57773 0.659386 3.14992 0.706553 2.7485 0.846296C2.34709 0.986039 1.98247 1.21474 1.68192 1.51529C1.38137 1.81584 1.15266 2.18046 1.01292 2.58188C0.873179 2.98329 0.826012 3.41111 0.874949 3.83332V12.1667C0.826012 12.5889 0.873179 13.0167 1.01292 13.4181C1.15266 13.8195 1.38137 14.1841 1.68192 14.4847C1.98247 14.7852 2.34709 15.0139 2.7485 15.1537C3.14992 15.2934 3.57773 15.3406 3.99995 15.2917H13.9999C14.4222 15.3406 14.85 15.2934 15.2514 15.1537C15.6528 15.0139 16.0174 14.7852 16.318 14.4847C16.6185 14.1841 16.8472 13.8195 16.987 13.4181C17.1267 13.0167 17.1739 12.5889 17.125 12.1667V3.83332C17.1739 3.41111 17.1267 2.98329 16.987 2.58188C16.8472 2.18046 16.6185 1.81584 16.318 1.51529C16.0174 1.21474 15.6528 0.986039 15.2514 0.846296C14.85 0.706553 14.4222 0.659386 13.9999 0.708323ZM2.12495 6.95832H8.37495V9.87499H2.12495V6.95832ZM9.62495 6.95832H15.8749V9.87499H9.62495V6.95832ZM3.99995 1.95832H13.9999C15.3141 1.95832 15.8749 2.51916 15.8749 3.83332V5.70832H2.12495V3.83332C2.12495 2.51916 2.68578 1.95832 3.99995 1.95832ZM2.12495 12.1667V11.125H8.37495V14.0417H3.99995C2.68578 14.0417 2.12495 13.4808 2.12495 12.1667ZM13.9999 14.0417H9.62495V11.125H15.8749V12.1667C15.8749 13.4808 15.3141 14.0417 13.9999 14.0417Z" fill="currentColor" />
                    </svg>
                );
            default:
                return (
                    <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={style === view ? { fill: '#2DC0EF', fillOpacity: 1 } : { fill: '#dedede', fillOpacity: 0.2 }}>
                        <path d="M13.9999 0.708323H3.99995C3.57773 0.659386 3.14992 0.706553 2.7485 0.846296C2.34709 0.986039 1.98247 1.21474 1.68192 1.51529C1.38137 1.81584 1.15266 2.18046 1.01292 2.58188C0.873179 2.98329 0.826012 3.41111 0.874949 3.83332V12.1667C0.826012 12.5889 0.873179 13.0167 1.01292 13.4181C1.15266 13.8195 1.38137 14.1841 1.68192 14.4847C1.98247 14.7852 2.34709 15.0139 2.7485 15.1537C3.14992 15.2934 3.57773 15.3406 3.99995 15.2917H13.9999C14.4222 15.3406 14.85 15.2934 15.2514 15.1537C15.6528 15.0139 16.0174 14.7852 16.318 14.4847C16.6185 14.1841 16.8472 13.8195 16.987 13.4181C17.1267 13.0167 17.1739 12.5889 17.125 12.1667V3.83332C17.1739 3.41111 17.1267 2.98329 16.987 2.58188C16.8472 2.18046 16.6185 1.81584 16.318 1.51529C16.0174 1.21474 15.6528 0.986039 15.2514 0.846296C14.85 0.706553 14.4222 0.659386 13.9999 0.708323ZM2.12495 6.95832H8.37495V9.87499H2.12495V6.95832ZM9.62495 6.95832H15.8749V9.87499H9.62495V6.95832ZM3.99995 1.95832H13.9999C15.3141 1.95832 15.8749 2.51916 15.8749 3.83332V5.70832H2.12495V3.83332C2.12495 2.51916 2.68578 1.95832 3.99995 1.95832ZM2.12495 12.1667V11.125H8.37495V14.0417H3.99995C2.68578 14.0417 2.12495 13.4808 2.12495 12.1667ZM13.9999 14.0417H9.62495V11.125H15.8749V12.1667C15.8749 13.4808 15.3141 14.0417 13.9999 14.0417Z" fill="currentColor" />
                    </svg>
                );
        }
    };

    const handleAction = (action, content = null, oldContent = null, docId = null, newlyAddedRow = null, selectionAction = null, isDisableRefresh) => {
        //to send nativeHeaderShow event to native side
        // if (action === "onConfirm") {
        //     dispatch(clickOnPrimaryAction(true))
        // }

        let sendActionObj = {
            messageType: MessageTypeConstants.MESSAGE_TYPE_CATALOG,
            message: {
                controlId: options.controlId,
                tabId: parentTabId ? parentTabId : options.tabId,
                parentDocId: options?.docId || parentDocId,
                selectedAction: selectionAction,
                action,
                content,
                oldContent,
                docId,
                sectionId: options?.sectionId || null,
                tz: user?.userTimezone || defaultUserTimezone,
            },
            messageId: uuidGenerator(),
        };

        dispatch(setTabState(false))
        if (isDisableRefresh) dispatch(setBotWaitLoaderWithShimmer(true));
        if (action !== "onDelete" && !isDisableRefresh) dispatch(setBotWaitLoaderWithShimmer(true));
        if (newlyAddedRow) sendActionObj = { ...sendActionObj, newRow: newlyAddedRow }
        processSendMessage(sendActionObj);
        dispatch(setAddManuallayRow(false));
    };

    const handleMicroAppClick = (botData) => {
        // sendToNative("handleBotAction", botData);
    };

        const handleCreateConversation = (item) => {
        
            dispatch(clearSelectedConversation());
            dispatch(setSelectedBot(item));
            dispatch(setBotWaitLoader(true));
            if (selectedBotDetails?.botName === item?.botName) {
              dispatch(setSelectedBot(item));
              updateDataInStorage(LFStorageKeys.SELECTED_BOT, JSON.stringify(item));
            }
            if (item?.customFrontEnd) {
              dispatch(onHomeClick(true));
            }
            if (!item.conversational) {
              dispatch(setSelectedOnetoOneChat(false))
            }
        
        
            dispatch(setTabState(true));
            dispatch(withOutInitSocket(true))
            updateDataInStorage(LFStorageKeys.HEADER_TITLE, item?.botName);
            const activeItemValue = `App Marketplace-${item?.botName}`;
            // Save all relevant data for persistence
            updateDataInStorage(LFStorageKeys.ACTIVE_ITEM, activeItemValue);
            updateDataInStorage(LFStorageKeys.SELECTED_BOT, JSON.stringify(item));
        
            if (item?.botName) {
              window.document.title = item?.botName;
              dispatch(setHeaderTitle(item?.botName))
            }
        
            dispatch(resetTabStack());
            dispatch(setCloseAssistantBot());
            dispatch(setHideChatwidget(true));
            dispatch(createChatBotConversation(item, user?.userId));
            dispatch(setHideDefaultHomepageBot());
            if (!item?.customFrontEnd) {
              dispatch(onHomeClick(false));
            }
              navigate("/app/chats");
          };

    const pageChangeRequestedRef = useRef(false);

    const handlePageChange = useCallback(
        (event, page, scrollTriggered = false) => {
            pageChangeRequestedRef.current = true;
            if (scrollTriggered) {
                setIsInfiniteLoading(true);
                isScrollRequestRef.current = true;
            } else {
                isScrollRequestRef.current = false;
            }
            handleAction("pageChange", { newPage: page }, null, null, null, true);
        },
        [handleAction]
    );

    const startScrollCooldown = useCallback(() => {
        scrollCooldownRef.current = true;
        if (scrollCooldownTimerRef.current) {
            window.clearTimeout(scrollCooldownTimerRef.current);
        }
        scrollCooldownTimerRef.current = window.setTimeout(() => {
            scrollCooldownRef.current = false;
            scrollCooldownTimerRef.current = null;
        }, SCROLL_COOLDOWN_MS);
    }, []);


    const renderMicroAppCatalog = () => {
        if (!Array.isArray(localRows)) return null;

        console.log('localRows ==> ', localRows);
        return (
            <div className="flex flex-col gap-2">
                {localRows
                    .filter((row) => !row?.rowOptions?.deleted)
                    .map((row) => {
                        const [rowKey, rowData] = Object.entries(row)[0];
                        const { fields } = rowData;

                        const botIconField = fields?.find((f) => f.id === 'microappsCatalog_doc_botIconUrl');
                        const botNameField = fields?.find((f) => f.title === 'Bot Name');
                        const descriptionField = fields?.find((f) => f.title === 'Description');
                        const botIdField = fields?.find((f) => f.title === 'Bot ID');
                        const conversationIdField = fields?.find((f) => f.title === 'Conversation ID');
                        const customeFrontEnd = fields?.find((f) => f.title === 'Custom Front End');
                        const conversational = fields?.find((f) => f.title === 'Conversational');

                        const botData = {
                            botId: botIdField?.value,
                            botName: botNameField?.value,
                            description: descriptionField?.value,
                            iconUrl: botIconField?.value,
                            conversationId: conversationIdField?.value,
                            customFrontEnd: customeFrontEnd?.value,
                            conversational: conversational?.value,
                        };

                        return (
                            <div
                                className="cursor-pointer bg-white rounded-[10px] p-[15px] shadow-[0_4px_20px_0px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_0px_rgba(0,0,0,0.08)] transition-shadow duration-200"
                                key={rowKey}
                                onClick={() => handleCreateConversation(botData)}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="relative w-fit">
                                        <span className="w-[40px] h-[40px] rounded-[8px] block overflow-hidden flex-shrink-0">
                                            {botData.iconUrl ? (
                                                <div className="aspect-square overflow-hidden rounded-[2px]">
                                                    <img
                                                        src={`${config.getBotLogoUrl}${botData.iconUrl}`}
                                                        alt={botData.botName}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.src = ImagePath.botDefault;
                                                        }}
                                                    />
                                                </div>

                                            ) : (
                                                <img
                                                    src={ImagePath.botDefault}
                                                    alt={botData.botName}
                                                    className="w-[40px] h-[40px] rounded-[8px] object-cover"
                                                />
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-[14px] text-black font-bold leading-tight">
                                            {botData.botName || ''}
                                        </h4>
                                        <p className="text-[13px] text-[#4a4a4a] mt-[3px] leading-[16px] font-normal ">
                                            {botData.description || ''}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
            </div>
        );
    };


    useEffect(() => {
        if (!actualRows) {
            setLocalRows([]);
            return;
        }

        const currentPage = options?.pages?.currentPage ?? 1;

        pageCacheRef.current[currentPage] = actualRows;

        const mergedRows = Object.keys(pageCacheRef.current)
            .map((page) => Number(page))
            .filter((page) => !Number.isNaN(page))
            .sort((a, b) => a - b)
            .flatMap((page) => pageCacheRef.current[page] || []);

        setLocalRows(mergedRows);
        isScrollRequestRef.current = false;
        setIsInfiniteLoading(false);
    }, [actualRows, options?.pages?.currentPage, options]);


    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const pages = options?.pages;
            if (scrollCooldownRef.current || !pages?.currentPage || !pages?.totalPages) return;
            if (pages.currentPage >= pages.totalPages) return;

            const { scrollTop, clientHeight, scrollHeight } = container;
            if (scrollHeight - scrollTop <= clientHeight + 100) {
                startScrollCooldown();
                handlePageChange(null, pages.currentPage + 1, true);
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [options?.pages, handlePageChange, startScrollCooldown]);


    return (
        <>
            {options?.domainBanners && <MobileBanner />}
            <CustomToolbar
                options={options}
                renderButtons={renderButtons}
                handleAction={handleAction}
                messageType={messageType}
                parentTabId={parentTabId}
                conversation={conversation}
            />

            {/* Content Area */}
            <Box
                ref={scrollContainerRef}
                className='conversation-list-scroll w-[749px] px-[71px]'
            >
                {/* Render MicroApps Catalog or Conversation List */}
                <>
                    <div className='align-center'>
                        {options?.emptyStateMessage
                            && options.emptyStateMessage != ""
                            && localRows.length == 0 && (
                                <div className="flex flex-col items-center justify-center py-16">
                                    <CachedImage
                                        image={ImagePath.emptyTable}
                                        imgKey="catalog-empty"
                                        alt='catalog-empty'
                                        className="w-[140px] h-[100px]"
                                    />
                                    <div className="text-sm font-bold text-center text-black mt-2">
                                        {options.emptyStateMessage}
                                    </div>
                                </div>
                            )}
                    </div>
                    {renderMicroAppCatalog()}
                </>

                {isInfiniteLoading && options?.pages?.currentPage < options?.pages?.totalPages && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default memo(FMCatalog);
